import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Typography, Badge, Tooltip, Popper, ClickAwayListener } from '@mui/material';
import { Add, Assignment, StickyNote2, Person, Link as LinkIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CalendarTask from './CalendarTask';
import AddNoteModal from './AddNoteModal';
import NotesModal from './NotesModal';
import TicketsModal from './TicketsModal';
import { useDispatch } from 'react-redux';
import { deleteCalendarNote, fetchCalendarData } from '../../../store/calendarSlice';
import { toast } from 'react-toastify';
import './CalendarView.scss';

const CalendarColumnGrid = ({ dates, headers, tasks, notes = [], onTaskClick, onCreateTask, projectId, dateRange, filters, colorCodeOption }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeMenuDate, setActiveMenuDate] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedDateNotes, setSelectedDateNotes] = useState([]);
  const [selectedNoteForEdit, setSelectedNoteForEdit] = useState(null);
  const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
  const [selectedDateTickets, setSelectedDateTickets] = useState([]);
  const menuRefs = useRef({});
  const addIconRefs = useRef({});

  useEffect(() => {
    if (!activeMenuDate) return;

    const handleClickOutside = (e) => {
      const menuRef = menuRefs.current[activeMenuDate];
      const addIconRef = addIconRefs.current[activeMenuDate];
      
      if (menuRef && !menuRef.contains(e.target) && 
          addIconRef && !addIconRef.contains(e.target)) {
        setActiveMenuDate(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenuDate]);

  const tasksByDate = useMemo(() => {
    const map = {};
    tasks.forEach(task => {
      const dateKey = task.startDate;
      if (dateKey) {
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(task);
      }
    });
    return map;
  }, [tasks]);

  const notesByDate = useMemo(() => {
    const map = {};
    notes.forEach(note => {
      if (note.date) {
        if (!map[note.date]) map[note.date] = [];
        map[note.date].push(note);
      }
    });
    return map;
  }, [notes]);

  const handleMenuClick = (dateString) => {
    setActiveMenuDate(activeMenuDate === dateString ? null : dateString);
  };

  const handleClickAway = () => {
    if (activeMenuDate) {
      setActiveMenuDate(null);
    }
  };

  const handleMenuItemClick = (action, dateString) => {
    setActiveMenuDate(null);
    if (action === 'Create Task' && onCreateTask) {
      onCreateTask(dateString);
    } else if (action === 'Add Note') {
      setSelectedDate(dateString);
      setIsNoteModalOpen(true);
    }
  };

  const handleNoteEdit = (note) => {
    setSelectedNoteForEdit(note);
    setIsNotesModalOpen(false);
    setIsNoteModalOpen(true);
  };

  const handleNoteDelete = async (note) => {
    try {
      await dispatch(deleteCalendarNote({ noteId: note.id })).unwrap();
      toast.success(t('calendar.noteDeletedSuccess'));
      
      if (dateRange) {
        dispatch(fetchCalendarData({ 
          projectId, 
          fromDate: dateRange.fromDate, 
          toDate: dateRange.toDate, 
          filters 
        }));
      }
    } catch (error) {
      toast.error(error || t('calendar.failedToDeleteNote'));
    }
  };

  return (
    <Box className="calendar-content" sx={{ position: 'relative' }}>
      <Box className="calendar-week-header" data-columns={dates.length}>
        {headers.map((header, index) => (
          <Box key={index} className="calendar-week-day-name">
            {header}
          </Box>
        ))}
      </Box>

      <Box className="calendar-week-columns" data-columns={dates.length}>
        {dates.map((dateString, dayIndex) => {
          const dayTasks = tasksByDate[dateString] || [];
          const dayNotes = notesByDate[dateString] || [];
          const menuOpen = activeMenuDate === dateString;
          const maxCells = 10;
          const maxVisibleTasks = 9;
          const hasMoreTasks = dayTasks.length > maxVisibleTasks;
          const tasksToShow = Math.min(dayTasks.length, maxVisibleTasks);
          const notesCellIndex = hasMoreTasks ? maxVisibleTasks : tasksToShow;
          
          return (
            <Box key={dayIndex} className="calendar-week-column">
              <Box 
                ref={(el) => { if (el) addIconRefs.current[dateString] = el; }}
                className={`calendar-day-add-icon ${menuOpen ? 'calendar-day-add-icon-active' : ''}`}
                onClick={() => handleMenuClick(dateString)}
              >
                <Add />
              </Box>
              {menuOpen && (
                <Popper 
                  open={menuOpen} 
                  anchorEl={addIconRefs.current[dateString]} 
                  placement="bottom"
                  modifiers={[
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                      },
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                      options: {
                        boundary: 'viewport',
                      },
                    },
                  ]}
                  style={{ zIndex: 1300 }}
                >
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Box 
                      ref={(el) => { if (el) menuRefs.current[dateString] = el; }}
                      className="calendar-day-menu"
                    >
                      <Box 
                        className="calendar-day-menu-item"
                        onClick={() => handleMenuItemClick('Create Task', dateString)}
                      >
                        <Assignment className="calendar-menu-icon" />
                        <span>{t('calendar.createTask')}</span>
                      </Box>
                      <Box 
                        className="calendar-day-menu-item"
                        onClick={() => handleMenuItemClick('Add Note', dateString)}
                      >
                        <StickyNote2 className="calendar-menu-icon" />
                        <span>{t('calendar.addNote')}</span>
                      </Box>
                    </Box>
                  </ClickAwayListener>
                </Popper>
              )}
              {Array.from({ length: maxCells }).map((_, cellIndex) => {
                const task = cellIndex < tasksToShow ? dayTasks[cellIndex] : null;
                const showMoreTasks = cellIndex === maxVisibleTasks && hasMoreTasks;
                const remainingTasks = dayTasks.length - maxVisibleTasks;
                const showNotes = cellIndex === notesCellIndex && dayNotes.length > 0;
                
                return (
                  <Box key={cellIndex} className="calendar-week-cell">
                    {showMoreTasks && showNotes ? (
                      <>
                        <Typography 
                          className="calendar-day-more" 
                          sx={{ margin: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDateTickets(dayTasks);
                            setSelectedDate(dateString);
                            setIsTicketsModalOpen(true);
                          }}
                        >
                          +{remainingTasks} {t('calendar.more')}
                        </Typography>
                        <Tooltip 
                          title={
                            <Box className="calendar-note-tooltip-content">
                              <Typography className="calendar-note-text">
                                {dayNotes[0].text}
                              </Typography>
                              {dayNotes[0].assignee && dayNotes[0].assignee.name && (
                                <Box className="calendar-note-field">
                                  <Person className="calendar-note-icon" />
                                  <Typography className="calendar-note-label">
                                    <strong>{t('calendar.assignee')}</strong> {dayNotes[0].assignee.name}
                                  </Typography>
                                </Box>
                              )}
                              {dayNotes[0].linkedTicket && dayNotes[0].linkedTicket.title && (
                                <Box className="calendar-note-field">
                                  <LinkIcon className="calendar-note-icon" />
                                  <Typography className="calendar-note-label">
                                    <strong>{t('calendar.linkedTicket')}</strong> {dayNotes[0].linkedTicket.ticket_number && `${dayNotes[0].linkedTicket.ticket_number} `}{dayNotes[0].linkedTicket.title}
                                  </Typography>
                                </Box>
                              )}
                              {dayNotes.length > 1 && (
                                <Typography className="calendar-note-label" sx={{ mt: 1, fontStyle: 'italic' }}>
                                  +{dayNotes.length - 1} {dayNotes.length - 1 > 1 ? t('calendar.moreNotes') : t('calendar.moreNote')}
                                </Typography>
                              )}
                            </Box>
                          } 
                          arrow 
                          placement="top"
                          slotProps={{ 
                            tooltip: { className: 'app-tooltip' }
                          }}
                        >
                          <Box 
                            className="calendar-note-icon-wrapper"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDateNotes(dayNotes);
                              setSelectedDate(dateString);
                              setIsNotesModalOpen(true);
                            }}
                          >
                            <Badge 
                              badgeContent={dayNotes.length > 1 ? dayNotes.length : 0} 
                              color="primary"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '10px',
                                  height: '16px',
                                  minWidth: '16px',
                                  padding: '0 4px'
                                }
                              }}
                            >
                              <StickyNote2 className="calendar-note-icon-main" />
                            </Badge>
                          </Box>
                        </Tooltip>
                      </>
                    ) : showMoreTasks ? (
                      <Typography 
                        className="calendar-day-more"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDateTickets(dayTasks);
                          setSelectedDate(dateString);
                          setIsTicketsModalOpen(true);
                        }}
                      >
                        +{remainingTasks} {t('calendar.more')}
                      </Typography>
                    ) : task ? (
                      <CalendarTask task={task} onClick={() => onTaskClick(task)} colorCodeOption={colorCodeOption} />
                    ) : showNotes ? (
                      <Tooltip 
                        title={
                          <Box className="calendar-note-tooltip-content">
                            <Typography className="calendar-note-text">
                              {dayNotes[0].text}
                            </Typography>
                            {dayNotes[0].assignee && dayNotes[0].assignee.name && (
                              <Box className="calendar-note-field">
                                <Person className="calendar-note-icon" />
                                <Typography className="calendar-note-label">
                                  <strong>{t('calendar.assignee')}</strong> {dayNotes[0].assignee.name}
                                </Typography>
                              </Box>
                            )}
                            {dayNotes[0].linkedTicket && dayNotes[0].linkedTicket.title && (
                              <Box className="calendar-note-field">
                                <LinkIcon className="calendar-note-icon" />
                                <Typography className="calendar-note-label">
                                  <strong>{t('calendar.linkedTicket')}</strong> {dayNotes[0].linkedTicket.ticket_number && `${dayNotes[0].linkedTicket.ticket_number} `}{dayNotes[0].linkedTicket.title}
                                </Typography>
                              </Box>
                            )}
                            {dayNotes.length > 1 && (
                              <Typography className="calendar-note-label" sx={{ mt: 1, fontStyle: 'italic' }}>
                                +{dayNotes.length - 1} {dayNotes.length - 1 > 1 ? t('calendar.moreNotes') : t('calendar.moreNote')}
                              </Typography>
                            )}
                          </Box>
                        } 
                        arrow 
                        placement="top"
                        slotProps={{ 
                          tooltip: { className: 'app-tooltip' }
                        }}
                      >
                        <Box 
                          className="calendar-note-icon-wrapper"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDateNotes(dayNotes);
                            setSelectedDate(dateString);
                            setIsNotesModalOpen(true);
                          }}
                        >
                          <Badge 
                            badgeContent={dayNotes.length > 1 ? dayNotes.length : 0} 
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '10px',
                                height: '16px',
                                minWidth: '16px',
                                padding: '0 4px'
                              }
                            }}
                          >
                            <StickyNote2 className="calendar-note-icon-main" />
                          </Badge>
                        </Box>
                      </Tooltip>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>

      <AddNoteModal
        open={isNoteModalOpen}
        onClose={() => {
          setIsNoteModalOpen(false);
          setSelectedNoteForEdit(null);
        }}
        projectId={projectId}
        dateRange={dateRange}
        filters={filters}
        note={selectedNoteForEdit}
        noteDate={selectedDate}
        mode={selectedNoteForEdit ? 'edit' : 'create'}
      />

      <NotesModal
        open={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        notes={selectedDateNotes}
        date={selectedDate}
        onEdit={handleNoteEdit}
        onDelete={handleNoteDelete}
      />

      <TicketsModal
        open={isTicketsModalOpen}
        onClose={() => setIsTicketsModalOpen(false)}
        tickets={selectedDateTickets}
        date={selectedDate}
        colorCodeOption={colorCodeOption}
      />
    </Box>
  );
};

export default CalendarColumnGrid;
